import { useState, useCallback, useRef } from "react";
import type { ConfirmDialogConfig, ConfirmDialogState, UseConfirmDialogReturn } from "./types";

const initialState: ConfirmDialogState = {
  isOpen: false,
  title: "",
  description: "",
  confirmText: "OK",
  cancelText: "Cancel",
  destructive: false,
  onConfirm: () => {},
};

/**
 * Hook for managing confirm dialog state.
 * Provides an async confirm() function similar to window.confirm().
 *
 * @example
 * ```tsx
 * const { confirm, dialogProps } = useConfirmDialog();
 *
 * const handleDelete = async () => {
 *   const confirmed = await confirm({
 *     title: "Delete item?",
 *     description: "This action cannot be undone.",
 *     confirmText: "Delete",
 *     destructive: true,
 *   });
 *   if (confirmed) {
 *     // Perform delete
 *   }
 * };
 *
 * return (
 *   <>
 *     <Button onClick={handleDelete}>Delete</Button>
 *     <ConfirmDialog {...dialogProps} />
 *   </>
 * );
 * ```
 */
export function useConfirmDialog(): UseConfirmDialogReturn {
  const [dialogState, setDialogState] = useState<ConfirmDialogState>(initialState);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((config: ConfirmDialogConfig): Promise<boolean> => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setDialogState({
        isOpen: true,
        title: config.title,
        description: config.description,
        confirmText: config.confirmText ?? "OK",
        cancelText: config.cancelText ?? "Cancel",
        destructive: config.destructive ?? false,
        onConfirm: () => {
          resolve(true);
          resolveRef.current = null;
          // Only set isOpen to false, keep other values to prevent flash during close animation
          setDialogState((prev) => ({ ...prev, isOpen: false }));
        },
      });
    });
  }, []);

  const closeDialog = useCallback(() => {
    if (resolveRef.current) {
      resolveRef.current(false);
      resolveRef.current = null;
    }
    // Only set isOpen to false, keep other values to prevent flash during close animation
    setDialogState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        closeDialog();
      }
    },
    [closeDialog]
  );

  return {
    dialogState,
    confirm,
    closeDialog,
    dialogProps: {
      open: dialogState.isOpen,
      onOpenChange: handleOpenChange,
      title: dialogState.title,
      description: dialogState.description,
      confirmText: dialogState.confirmText,
      cancelText: dialogState.cancelText,
      onConfirm: dialogState.onConfirm,
      destructive: dialogState.destructive,
    },
  };
}
